import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip} from "recharts";
import {chartPalette} from "../../components/chartColors";
import {InsightsLegend, LegendEntry} from "../../kit/InsightsLegend";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {InsightsTooltipPanel, TooltipRow, TooltipTitle} from "../../kit/InsightsTooltip";
import {SeriesCard} from "../../kit/SeriesCard";
import {CHART_DRAW_DURATION, CHART_HEIGHT} from "../../kit/insightsChart";
import type {InsightsLoading} from "../../kit/insightsLoading";

const INNER_RADIUS = "52%";
const OUTER_RADIUS = "86%";
/** Below this share the arc is too narrow to hold its own figure, so only the legend names it. */
const ARC_LABEL_MIN_SHARE = 7;
const RADIAN = Math.PI / 180;

export interface ShareRow {
    key: string;
    label: string;
    value: number;
    compareValue?: number | null;
    /** Where the key sits in its own fixed set, so a slice keeps its colour when another one is absent. */
    rank?: number;
}

interface Slice extends ShareRow {
    color: string;
    figure: number;
    share: number;
}

interface ArcShareLabelProps {
    cx?: number;
    cy?: number;
    midAngle?: number;
    innerRadius?: number;
    outerRadius?: number;
    index?: number;
    /** Not named `fill`: recharts hands a cloned label element the slice's own fill, which would hide the text. */
    textColor: string;
    slices: Slice[];
    formatShare: (share: number) => string;
}

/** The share sits inside its own arc, which is what makes the ring read as parts of one whole. */
function ArcShareLabel({cx, cy, midAngle, innerRadius, outerRadius, index, textColor, slices, formatShare}: ArcShareLabelProps) {
    const slice = index == null ? undefined : slices[index];

    if (slice == null || cx == null || cy == null || midAngle == null || slice.share < ARC_LABEL_MIN_SHARE) {
        return null;
    }

    const radius = Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.5;

    return (
        <text
            x={cx + radius * Math.cos(-midAngle * RADIAN)}
            y={cy + radius * Math.sin(-midAngle * RADIAN)}
            fill={textColor}
            textAnchor={"middle"}
            dominantBaseline={"central"}
            fontSize={11}
            fontWeight={600}
            pointerEvents={"none"}
        >
            {formatShare(slice.share)}
        </text>
    );
}

interface ShareTooltipProps {
    active?: boolean;
    payload?: Array<{payload?: Slice}>;
    period?: string;
    valueLabel: string;
    shareLabel: string;
    format: (value: number) => string;
    formatShare: (share: number) => string;
}

function ShareTooltip({active, payload, period, valueLabel, shareLabel, format, formatShare}: ShareTooltipProps) {
    const slice = payload?.[0]?.payload;

    if (!active || slice == null) {
        return null;
    }

    return (
        <InsightsTooltipPanel>
            <TooltipTitle title={slice.label} subtitle={period}/>

            <TooltipRow swatch={slice.color} label={valueLabel} value={format(slice.figure)}/>
            <TooltipRow label={shareLabel} value={formatShare(slice.share)}/>
        </InsightsTooltipPanel>
    );
}

interface Props {
    title: string;
    hint: string;
    rows: ShareRow[];
    loading?: InsightsLoading;
    comparing?: boolean;
    /** How one slice's figure reads in full — "36 guests", "12 reservations". */
    format: (value: number) => string;
    /** What the figure is, for the table's value column and the hover. */
    valueLabel: string;
    empty: string;
}

/**
 * A set that adds up to one whole — account types, age categories, genders — drawn as a ring, with the
 * comparison period as a second ring beside it rather than a paler one behind it: two rings of the same
 * size compare shapes, which is the only thing worth comparing once the two periods differ in size.
 */
export function CrmShareCard({title, hint, rows, loading, comparing = false, format, valueLabel, empty}: Props) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();

    const locale = i18n.resolvedLanguage || i18n.language;
    const shareLabel = t("Share");
    const selectedLabel = t("Selected period");
    const compareLabel = t("Comparison period");
    const formatShare = (share: number) => `${share.toLocaleString(locale, {maximumFractionDigits: 1})}%`;

    // A key's place in its own fixed set beats its place in this period's answer: a gender nobody
    // reported must not hand its colour to the next one along.
    const palette = [
        ...chartPalette(theme),
        theme.palette.secondary.main,
        theme.palette.warning.dark,
        theme.palette.grey[400],
    ];
    const colorOf = (row: ShareRow, index: number) => palette[(row.rank ?? index) % palette.length];

    const hasCompare = comparing && rows.some(row => row.compareValue != null);
    const hasData = rows.some(row => row.value > 0 || (row.compareValue ?? 0) > 0);

    const slicesOf = (comparison: boolean): Slice[] => {
        const measured = rows
            .map((row, index) => ({
                ...row,
                color: colorOf(row, index),
                figure: comparison ? row.compareValue ?? 0 : row.value,
                share: 0,
            }))
            .filter(slice => slice.figure > 0);
        const total = measured.reduce((sum, slice) => sum + slice.figure, 0);

        return measured.map(slice => ({...slice, share: total > 0 ? (slice.figure / total) * 100 : 0}));
    };

    const selected = slicesOf(false);
    const compared = hasCompare ? slicesOf(true) : [];
    const selectedTotal = selected.reduce((sum, slice) => sum + slice.figure, 0);

    // Re-running the draw on every hover would be noise; it plays once per change of what is charted.
    const [animate, setAnimate] = React.useState(true);
    const shape = `${rows.length}:${hasCompare}`;

    React.useEffect(() => {
        setAnimate(true);
        const timer = window.setTimeout(() => setAnimate(false), CHART_DRAW_DURATION + 400);

        return () => window.clearTimeout(timer);
    }, [shape]);

    const columns: InsightsColumn<ShareRow>[] = [
        {key: "label", label: title, kind: "label", cell: row => row.label},
        {key: "value", label: valueLabel, kind: "value", cell: row => format(row.value)},
        {
            key: "share",
            label: shareLabel,
            kind: "detail",
            cell: row => formatShare(selectedTotal > 0 ? (row.value / selectedTotal) * 100 : 0),
        },
        ...(hasCompare ? [{
            key: "compare",
            label: compareLabel,
            kind: "compare" as const,
            cell: (row: ShareRow) => row.compareValue == null ? "—" : format(row.compareValue),
        }] : []),
    ];

    const ring = (slices: Slice[], period: string | undefined) => (
        <Box sx={{minWidth: 0}}>
            {period != null && (
                <Typography component={"div"} align={"center"} variant={"caption"} color={"text.secondary"} fontWeight={600}>
                    {period}
                </Typography>
            )}

            <Box sx={{width: "100%", minWidth: 0, height: CHART_HEIGHT}}>
                {slices.length === 0 ? (
                    <Stack alignItems={"center"} justifyContent={"center"} sx={{height: "100%", px: 3}}>
                        <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                            {t("No data in this period")}
                        </Typography>
                    </Stack>
                ) : (
                    <ResponsiveContainer width={"100%"} height={"100%"} minWidth={0}>
                        <PieChart margin={{top: 12, right: 12, bottom: 12, left: 12}}>
                            <Pie
                                data={slices}
                                dataKey={"figure"}
                                nameKey={"label"}
                                innerRadius={INNER_RADIUS}
                                outerRadius={OUTER_RADIUS}
                                paddingAngle={2}
                                cornerRadius={3}
                                stroke={"none"}
                                labelLine={false}
                                label={(
                                    <ArcShareLabel
                                        textColor={theme.palette.common.white}
                                        slices={slices}
                                        formatShare={formatShare}
                                    />
                                )}
                                isAnimationActive={animate}
                                animationDuration={CHART_DRAW_DURATION}
                                animationEasing={"ease-out"}
                            >
                                {slices.map(slice => (
                                    <Cell key={slice.key} fill={slice.color}/>
                                ))}
                            </Pie>

                            <RechartsTooltip
                                isAnimationActive={false}
                                offset={14}
                                wrapperStyle={{pointerEvents: "none", transition: "none", outline: "none", zIndex: 10}}
                                content={(
                                    <ShareTooltip
                                        period={period}
                                        valueLabel={valueLabel}
                                        shareLabel={shareLabel}
                                        format={format}
                                        formatShare={formatShare}
                                    />
                                )}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Box>
    );

    return (
        <SeriesCard
            title={title}
            hint={hint}
            loading={loading}
            empty={hasData ? undefined : (
                <Stack alignItems={"center"} justifyContent={"center"} sx={{height: CHART_HEIGHT, px: 3}}>
                    <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                        {empty}
                    </Typography>
                </Stack>
            )}
            legend={rows.length < 2 ? undefined : (
                <InsightsLegend>
                    {rows.map((row, index) => (
                        <LegendEntry key={row.key} shape={"bar"} color={colorOf(row, index)} label={row.label}/>
                    ))}
                </InsightsLegend>
            )}
            table={<InsightsTable label={title} columns={columns} rows={rows} rowKey={row => row.key}/>}
            chart={(
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: hasCompare ? {xs: "1fr", sm: "repeat(2, minmax(0, 1fr))"} : "1fr",
                        gap: 1,
                        minWidth: 0,
                    }}
                >
                    {ring(selected, hasCompare ? selectedLabel : undefined)}
                    {hasCompare && ring(compared, compareLabel)}
                </Box>
            )}
        />
    );
}

export default CrmShareCard;
